import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { SESSION_EXPIRED_EVENT, auth } from "@/api/client";
import { Layout } from "@/components/Layout";
import { Spinner } from "@/components/ui";
import { LoginPage } from "@/pages/Login";

// Route-level code splitting: the login screen and shell load immediately, and
// the heavy chart pages arrive only when navigated to.
const PortfolioPage = lazy(() => import("@/pages/Portfolio"));
const HomePage = lazy(() => import("@/pages/Home"));
const DashboardsPage = lazy(() => import("@/pages/Dashboards"));
const EventsPage = lazy(() => import("@/pages/Events"));
const CrossingsPage = lazy(() => import("@/pages/Crossings"));
const LoadCellPage = lazy(() => import("@/pages/LoadCell"));
const SpanPage = lazy(() => import("@/pages/Span"));
const EventDetailPage = lazy(() => import("@/pages/EventDetail"));
const DataViewerPage = lazy(() => import("@/pages/DataViewer"));
const AlarmsPage = lazy(() => import("@/pages/Alarms"));
const FatiguePage = lazy(() => import("@/pages/Fatigue"));
const MapPage = lazy(() => import("@/pages/MapView"));
const SystemPage = lazy(() => import("@/pages/System"));
const AdminPage = lazy(() => import("@/pages/Admin"));

function RequireAuth({ children }: { children: JSX.Element }) {
  const location = useLocation();
  if (!auth.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

/**
 * When the session cannot be renewed (idle a month, signed out elsewhere),
 * go to the login page - with a note, and back to this page afterwards -
 * rather than leaving every panel showing "token has expired".
 */
function useSessionExpiry() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  useEffect(() => {
    const onExpired = () => {
      if (location.pathname === "/login") return;
      queryClient.clear();
      navigate("/login", { replace: true, state: { from: location, expired: true } });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [navigate, location, queryClient]);
}

export default function App() {
  useSessionExpiry();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route
          path="/"
          element={
            <Suspense fallback={<Spinner />}>
              <PortfolioPage />
            </Suspense>
          }
        />
        <Route
          path="/overview"
          element={
            <Suspense fallback={<Spinner />}>
              <HomePage />
            </Suspense>
          }
        />
        <Route
          path="/dashboards"
          element={
            <Suspense fallback={<Spinner />}>
              <DashboardsPage />
            </Suspense>
          }
        />
        <Route
          path="/crossings"
          element={
            <Suspense fallback={<Spinner />}>
              <CrossingsPage />
            </Suspense>
          }
        />
        <Route
          path="/devices/:device"
          element={
            <Suspense fallback={<Spinner />}>
              <LoadCellPage />
            </Suspense>
          }
        />
        <Route
          path="/spans/:span"
          element={
            <Suspense fallback={<Spinner />}>
              <SpanPage />
            </Suspense>
          }
        />
        <Route
          path="/events"
          element={
            <Suspense fallback={<Spinner />}>
              <EventsPage />
            </Suspense>
          }
        />
        <Route
          path="/events/:code"
          element={
            <Suspense fallback={<Spinner />}>
              <EventDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/data"
          element={
            <Suspense fallback={<Spinner />}>
              <DataViewerPage />
            </Suspense>
          }
        />
        <Route
          path="/alarms"
          element={
            <Suspense fallback={<Spinner />}>
              <AlarmsPage />
            </Suspense>
          }
        />
        <Route
          path="/fatigue"
          element={
            <Suspense fallback={<Spinner />}>
              <FatiguePage />
            </Suspense>
          }
        />
        <Route
          path="/map"
          element={
            <Suspense fallback={<Spinner />}>
              <MapPage />
            </Suspense>
          }
        />
        <Route
          path="/admin"
          element={
            <Suspense fallback={<Spinner />}>
              <AdminPage />
            </Suspense>
          }
        />
        <Route
          path="/system"
          element={
            <Suspense fallback={<Spinner />}>
              <SystemPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
